import React from 'react';
import { toast } from 'react-toastify';
import toastConfig from '../config/Toast';
import useApi from '../api';
import useAnalytics from '../analytics/analytics';
import { formatRelativeTime } from '../utils/dateFormatter';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle, RefreshCw, File, Clock, Trash2 } from 'lucide-react';

interface FailedPagesDialogProps {
    open: boolean;
    websiteId: string;
    pages: any[];
    onClose: () => void;
    onSuccess?: () => void;
}

export default function FailedPagesDialog({ 
    open, 
    websiteId, 
    pages, 
    onClose, 
    onSuccess 
}: FailedPagesDialogProps) {
    const { post } = useApi();
    const { captureEvent } = useAnalytics();
    const [selectedFailedPages, setSelectedFailedPages] = React.useState<Set<string>>(new Set()); // Stores page IDs
    const [retrainingPages, setRetrainingPages] = React.useState<boolean>(false);
    const [deletingPages, setDeletingPages] = React.useState<boolean>(false);

    // Initialize selected pages when dialog opens
    React.useEffect(() => {
        if (open && pages.length > 0) {
            setSelectedFailedPages(new Set(pages.map(page => page.id)));
        }
    }, [open, pages]);

    const togglePageSelection = (id: string) => {
        setSelectedFailedPages(prev => {
            const newSelected = new Set(prev);
            if (newSelected.has(id)) {
                newSelected.delete(id);
            } else {
                newSelected.add(id);
            }
            return newSelected;
        });
    };

    const selectAllFailedPages = () => {
        setSelectedFailedPages(new Set(pages.map(page => page.id)));
    };

    const deselectAllFailedPages = () => {
        setSelectedFailedPages(new Set());
    };

    const retrainSelectedPages = async () => {
        if (selectedFailedPages.size === 0) {
            toast.error('No pages selected for retraining', toastConfig);
            return;
        }

        setRetrainingPages(true);
        try {
            const selectedIds = new Set(selectedFailedPages);
            const urls = pages.filter(p => selectedIds.has(p.id)).map(p => p.url);
            await post('/add-urls-to-index/', {
                website_id: websiteId,
                urls: urls
            });

            toast.success(`Retraining ${urls.length} page${urls.length > 1 ? 's' : ''} started`, toastConfig);
            captureEvent('retrain_failed_pages', {
                website_id: websiteId,
                page_count: urls.length
            });

            handleClose();
            onSuccess?.();

        } catch (error: any) {
            console.error('Error retraining pages:', error);
            toast.error(error.detail || 'Failed to retrain pages', toastConfig);
        } finally {
            setRetrainingPages(false);
        }
    };

    const deleteSelectedPages = async () => {
        if (selectedFailedPages.size === 0) {
            toast.error('No pages selected for deletion', toastConfig);
            return;
        }

        const count = selectedFailedPages.size;
        const confirmed = window.confirm(`Are you sure you want to delete ${count} page${count !== 1 ? 's' : ''}? This cannot be undone.`);
        if (!confirmed) return;

        setDeletingPages(true);
        try {
            const page_ids = Array.from(selectedFailedPages);
            await post(`/pages/bulk-action`, { page_ids, action: 'delete' });

            toast.success(`Deleted ${count} page${count !== 1 ? 's' : ''}`, toastConfig);
            handleClose();
            onSuccess?.();
        } catch (error: any) {
            console.error('Error deleting pages:', error);
            toast.error(error.detail || 'Failed to delete pages', toastConfig);
        } finally {
            setDeletingPages(false);
        }
    };

    const handleClose = () => {
        setSelectedFailedPages(new Set());
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center">
                        <AlertCircle className="w-5 h-5 mr-2 text-red-500" />
                        Failed Pages
                    </DialogTitle>
                    <DialogDescription>
                        Select the pages you want to retry or delete. Retrying will add them back to the training queue.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex items-center justify-between my-2">
                    <div className="space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={selectAllFailedPages}
                            disabled={pages.length === 0}
                        >
                            Select All
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={deselectAllFailedPages}
                            disabled={selectedFailedPages.size === 0}
                        >
                            Deselect All
                        </Button>
                    </div>
                    <div className="text-sm text-muted-foreground">
                        {selectedFailedPages.size} of {pages.length} selected
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto py-2">
                    {pages.length === 0 ? (
                        <div className="text-center py-4 text-muted-foreground">
                            No failed pages found
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {pages.map((page) => (
                                <div
                                    key={page.id}
                                    className={`flex items-center p-2 border rounded-md ${selectedFailedPages.has(page.id) ? 'border-border bg-accent/20' : ''
                                        }`}
                                >
                                    <Checkbox
                                        id={`page-${page.id}`}
                                        checked={selectedFailedPages.has(page.id)}
                                        onCheckedChange={() => togglePageSelection(page.id)}
                                        className="mr-2"
                                    />
                                    <div className="flex items-center space-x-2 overflow-hidden flex-1">
                                        <File className="w-4 h-4 flex-shrink-0" />
                                        <div className="truncate text-sm flex-1 max-w-[300px] md:max-w-[400px] lg:max-w-[500px]">{page.url}</div>
                                        {page.indexed_at && (
                                            <div className="text-xs text-muted-foreground whitespace-nowrap">
                                                <Clock className="w-3 h-3 inline mr-1" />
                                                {formatRelativeTime(page.indexed_at)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <DialogFooter className="mt-4">
                    <Button
                        variant="outline"
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="destructive"
                        onClick={deleteSelectedPages}
                        disabled={selectedFailedPages.size === 0 || deletingPages || retrainingPages}
                        className="ml-2"
                    >
                        {deletingPages ? (
                            <>
                                <Trash2 className="w-4 h-4 mr-2 animate-spin" />
                                Deleting...
                            </>
                        ) : (
                            <>
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete {selectedFailedPages.size} Page{selectedFailedPages.size !== 1 ? 's' : ''}
                            </>
                        )}
                    </Button>
                    <Button
                        variant="default"
                        onClick={retrainSelectedPages}
                        disabled={selectedFailedPages.size === 0 || retrainingPages || deletingPages}
                        className="ml-2"
                    >
                        {retrainingPages ? (
                            <>
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                Retraining...
                            </>
                        ) : (
                            <>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Retry {selectedFailedPages.size} Page{selectedFailedPages.size !== 1 ? 's' : ''}
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
} 